import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, Switch, Table, Tooltip } from '@surecompanies/core_components'
import type { TableProps } from '@surecompanies/core_components'
import type { ColumnType } from 'antd/es/table/interface'
import { Popconfirm } from 'antd'
import { useRouterContext } from 'components/RouterContext'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useCallback, useMemo, useState } from 'react'
import { convertToLocalFromUTCISODate } from 'helpers/datetime'
import {
  BenchmarkSnapshotItem,
  useBenchmarkSnapshots,
} from './hooks/useBenchmarkSnapshots'
import { UploadBenchmarkSnapshotModal } from './components/UploadBenchmarkSnapshotModal'
import { benchmarkPlansPath } from './paths'

export default function BenchmarkPlans() {
  const { router } = useRouterContext()
  const {
    snapshots,
    total,
    page,
    pageSize,
    isLoading,
    onDownload,
    isDownloading,
    onDelete,
    isDeleting,
    onToggleEnabled,
    isTogglingEnabled,
    handlePageChange,
  } = useBenchmarkSnapshots()

  const [downloadingIds, setDownloadingIds] = useState(() => new Set<number>())
  const [deletingIds, setDeletingIds] = useState(() => new Set<number>())
  const [togglingIds, setTogglingIds] = useState(() => new Set<number>())
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  const isAnyActionInFlight = isDownloading || isDeleting || isTogglingEnabled

  const handleModalClose = useCallback(() => {
    setIsUploadModalOpen(false)
  }, [])

  const handleUploadSuccess = useCallback(() => {
    handlePageChange(1)
  }, [handlePageChange])

  const handleDownload = useCallback(
    (snapshot: BenchmarkSnapshotItem) => {
      const id = snapshot.id
      setDownloadingIds((prev) => new Set(prev).add(id))
      onDownload(id, {
        onSettled: () => {
          setDownloadingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        },
      })
    },
    [onDownload]
  )

  const handleDelete = useCallback(
    (id: number) => {
      setDeletingIds((prev) => new Set(prev).add(id))
      onDelete(id, {
        onSettled: () => {
          setDeletingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        },
      })
    },
    [onDelete]
  )

  const handleToggleEnabled = useCallback(
    (id: number, isEnabled: boolean) => {
      setTogglingIds((prev) => new Set(prev).add(id))
      onToggleEnabled(
        { id, isEnabled },
        {
          onSettled: () => {
            setTogglingIds((prev) => {
              const next = new Set(prev)
              next.delete(id)
              return next
            })
          },
        }
      )
    },
    [onToggleEnabled]
  )

  const handleTableChange: TableProps<BenchmarkSnapshotItem>['onChange'] =
    useCallback(
      (pagination) => {
        handlePageChange(pagination.current ?? page, pagination.pageSize)
      },
      [handlePageChange, page]
    )

  const columns = useMemo<ColumnType<BenchmarkSnapshotItem>[]>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (value: string | undefined) => value ?? '—',
      },
      {
        title: 'Created At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) =>
          value ? convertToLocalFromUTCISODate(value) : '—',
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (value: string | undefined) => value ?? '—',
      },
      {
        title: 'Active',
        dataIndex: 'isEnabled',
        key: 'isEnabled',
        width: 90,
        render: (value: boolean, row: BenchmarkSnapshotItem) => (
          <Switch
            checked={value}
            loading={togglingIds.has(row.id)}
            disabled={isAnyActionInFlight}
            onChange={(checked) => handleToggleEnabled(row.id, checked)}
          />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, row: BenchmarkSnapshotItem) => (
          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Download">
              <Button
                type="default"
                icon={<DownloadOutlined />}
                loading={downloadingIds.has(row.id)}
                disabled={isAnyActionInFlight}
                onClick={() => handleDownload(row)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete snapshot"
              description="This will permanently remove the snapshot. Are you sure?"
              onConfirm={() => handleDelete(row.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              disabled={isAnyActionInFlight}
            >
              <Tooltip title="Delete">
                <Button
                  type="default"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingIds.has(row.id)}
                  disabled={isAnyActionInFlight}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [
      downloadingIds,
      deletingIds,
      togglingIds,
      isAnyActionInFlight,
      handleDownload,
      handleDelete,
      handleToggleEnabled,
    ]
  )

  return (
    <Page
      title="Benchmark Plans"
      breadcrumb={
        <BreadcrumbNav
          items={[{ title: 'Benchmark Plans', path: benchmarkPlansPath() }]}
        />
      }
      action={
        <>
          <Button
            icon={<UploadOutlined />}
            type="primary"
            onClick={() => setIsUploadModalOpen(true)}
            style={{ marginRight: 8 }}
          >
            Upload Snapshot
          </Button>
          <Button icon={<ArrowLeftOutlined />} onClick={() => router.goBack()}>
            Back
          </Button>
        </>
      }
    >
      <Table
        dataSource={snapshots}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: 'No benchmark plan snapshots found' }}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      <UploadBenchmarkSnapshotModal
        open={isUploadModalOpen}
        onClose={handleModalClose}
        onSuccess={handleUploadSuccess}
      />
    </Page>
  )
}
